import React from 'react';
import PropTypes from 'prop-types';
import { DropdownMenu, SegmentedControl, SparkIcon, TooltipTrigger } from '@spark';
import FieldLabel from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/FieldLabel';
import {
    CONTENT_TYPE_ITEMS,
    HELP_TEXTS,
    LANGUAGE_OPTIONS,
    TEXT_LENGTH_ITEMS,
    TEXT_LENGTH_OPTION_HELP,
    TONE_STYLE_OPTIONS,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';

const asLanguageList = (value) => {
    if (Array.isArray(value)) return value;
    return value ? [value] : [];
};

const selectedTone = (value) => {
    if (Array.isArray(value)) return value[0] || '';
    return value || '';
};

const textLengthItems = (showOptionHelp) =>
    showOptionHelp
        ? TEXT_LENGTH_ITEMS.map((item) => ({
              ...item,
              endIcon: (
                  <TooltipTrigger text={TEXT_LENGTH_OPTION_HELP[item.id]}>
                      <span className="gdai-field-label__help">
                          <SparkIcon slug="circle-help" size="small" />
                      </span>
                  </TooltipTrigger>
              ),
          }))
        : TEXT_LENGTH_ITEMS;

/** "Text length" Short/Medium/Long segmented control. */
export const TextLengthField = ({ value, onChange, showOptionHelp = false }) => (
    <div className="gdai-field">
        <FieldLabel text="Text length" help={HELP_TEXTS.textLength} />
        <SegmentedControl
            className="gdai-segmented"
            items={textLengthItems(showOptionHelp)}
            activeId={value}
            onChange={onChange}
            size="medium"
            fill
        />
    </div>
);

TextLengthField.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    showOptionHelp: PropTypes.bool,
};

/** "Content Type" Text Only / Text & Code / Code only segmented control. */
export const ContentTypeField = ({ value, onChange }) => (
    <div className="gdai-field">
        <FieldLabel text="Content Type" help={HELP_TEXTS.contentType} />
        <SegmentedControl
            className="gdai-segmented"
            items={CONTENT_TYPE_ITEMS}
            activeId={value}
            onChange={onChange}
            size="medium"
            fill
        />
    </div>
);

ContentTypeField.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
};

/** Multi-select "Supported languages" dropdown (Figma Additional Settings). */
export const LanguageField = ({ value, onChange }) => {
    const selected = asLanguageList(value);
    return (
        <div className="gdai-field">
            <FieldLabel text="Supported languages" help={HELP_TEXTS.language} />
            <DropdownMenu
                selectType="multi-select"
                size="medium"
                fill
                showLabel={false}
                placeholder="Select"
                tags={selected}
                value={selected}
                items={LANGUAGE_OPTIONS.map((option) => ({
                    label: option,
                    type: 'checkbox',
                    checked: selected.includes(option),
                }))}
                onSelect={(item) => {
                    const next = selected.includes(item.label)
                        ? selected.filter((language) => language !== item.label)
                        : [...selected, item.label];
                    onChange(next);
                }}
            />
        </div>
    );
};

LanguageField.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    onChange: PropTypes.func.isRequired,
};

/** Single-select "Tone & Style" dropdown; wire value is a one-element array. */
export const ToneStyleField = ({ value, onChange }) => {
    const selected = selectedTone(value);
    return (
        <div className="gdai-field">
            <FieldLabel text="Tone & Style" help={HELP_TEXTS.toneStyle} />
            <DropdownMenu
                selectType="single-select"
                size="medium"
                fill
                showLabel={false}
                placeholder="Select"
                text={selected || undefined}
                value={selected || undefined}
                items={TONE_STYLE_OPTIONS.map((option) => ({ label: option }))}
                onSelect={(item) => onChange([item.label])}
                showClearButton
                onClear={() => onChange([])}
            />
        </div>
    );
};

ToneStyleField.propTypes = {
    value: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired,
};
